import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  Collapse,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import NotificationsIcon from '@material-ui/icons/Notifications';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3)
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  title: {
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  listItem: {
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    '&:hover': {
      backgroundColor: theme.palette.grey[100]
    }
  },
  alertIcon: {
    minWidth: 40
  },
  timestamp: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  },
  details: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    fontSize: '0.875rem',
    fontFamily: 'monospace'
  },
  noAlerts: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary
  }
}));

const getAlertIcon = (severity) => {
  switch (severity) {
    case 'error':
      return <ErrorIcon color="error" />;
    case 'warning':
      return <WarningIcon color="action" style={{ color: '#ff9800' }} />;
    case 'info':
      return <InfoIcon color="primary" />;
    case 'success':
      return <CheckCircleIcon color="action" style={{ color: '#4caf50' }} />;
    default:
      return <InfoIcon />;
  }
};

const getAlertColor = (severity) => {
  switch (severity) {
    case 'error':
      return 'error';
    case 'warning':
      return 'default';
    case 'info':
      return 'primary';
    case 'success':
      return 'default';
    default:
      return 'default';
  }
};

const AlertItem = ({ alert }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  
  return (
    <ListItem className={classes.listItem}>
      <ListItemIcon className={classes.alertIcon}>
        {getAlertIcon(alert.severity)}
      </ListItemIcon>
      
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">
              {alert.message}
            </Typography>
            <Chip 
              label={alert.severity.toUpperCase()}
              size="small"
              color={getAlertColor(alert.severity)}
            />
          </Box>
        }
        secondary={
          <>
            <Typography className={classes.timestamp}>
              {new Date(alert.timestamp).toLocaleString()}
            </Typography>
            
            {alert.details && (
              <Collapse in={expanded}>
                <Box className={classes.details}>
                  {typeof alert.details === 'string' 
                    ? alert.details
                    : JSON.stringify(alert.details, null, 2)
                  }
                </Box>
              </Collapse>
            )}
          </>
        }
      />
      
      {alert.details && (
        <IconButton 
          size="small" 
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      )}
    </ListItem>
  );
};

const AlertsPanel = ({ alerts = [] }) => {
  const classes = useStyles();
  
  const sortedAlerts = [...alerts].sort((a, b) => 
    new Date(b.timestamp) - new Date(a.timestamp)
  );
  
  const criticalCount = alerts.filter(a => a.severity === 'error').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;
  
  return (
    <Paper className={classes.paper}>
      <Box className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          <NotificationsIcon />
          System Alerts
        </Typography>
        
        <Box display="flex" gap={1}>
          {criticalCount > 0 && (
            <Chip 
              label={`${criticalCount} Critical`}
              color="secondary"
              size="small"
            />
          )}
          {warningCount > 0 && (
            <Chip 
              label={`${warningCount} Warnings`}
              size="small"
            />
          )}
        </Box>
      </Box>
      
      {sortedAlerts.length === 0 ? (
        <Box className={classes.noAlerts}>
          <CheckCircleIcon style={{ fontSize: 48, color: '#4caf50', marginBottom: 16 }} />
          <Typography>All systems operational</Typography>
          <Typography variant="caption" color="textSecondary">
            No alerts at this time
          </Typography>
        </Box>
      ) : (
        <List>
          {sortedAlerts.map((alert, index) => (
            <React.Fragment key={alert.id || index}>
              <AlertItem alert={alert} />
              {index < sortedAlerts.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default AlertsPanel;

